// src/components/ServiceRequestForm.jsx

import React, { useState } from 'react';
import { Send, X } from 'lucide-react';

const ServiceRequestForm = ({ language = 'en', content }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
 
  // Safety check: If content is not provided, use fallback
  if (!content || !content[language]) {
    console.error('ServiceRequestForm: content prop is missing or invalid');
    return null; // Or return a loading state
  }

  const t = content[language].serviceRequest;

  // Additional safety check
  if (!t) {
    console.error('ServiceRequestForm: serviceRequest section not found in content');
    return null;
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const access_key = process.env.REACT_APP_WEB3FORMS_KEY;

    const formDataToSend = new FormData();
    formDataToSend.append('access_key', access_key); 
    formDataToSend.append('name', formData.name);
    formDataToSend.append('phone', formData.phone);
    formDataToSend.append('service', formData.service);
    formDataToSend.append('message', formData.message);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formDataToSend,
        headers: {
          'Accept': 'application/json',
        }
      });
   
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Form submission failed:', errorData);
        setError('There was an error submitting the form. Please try again later.');
        return;
      }

      const result = await response.json();
      if (result.success) {
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setFormData({ name: '', phone: '', service: '', message: '' });
          setIsOpen(false);
        }, 3000);
      } else {
        setError('There was an error submitting the form. Please try again later.');
      }
    } catch (err) {
      console.error('Error during fetch:', err);
      setError('There was an error submitting the form. Please try again later.');
    }
  };

  return (
    <>
      <style>{`
        .request-form-button {
          position: fixed;
          bottom: 115px;
          right: 30px;
          background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
          color: white;
          padding: 16px 28px;
          border-radius: 50px;
          border: none;
          cursor: pointer;
          font-weight: 600;
          font-size: 15px;
          box-shadow: 0 8px 24px rgba(255, 107, 53, 0.4);
          z-index: 999;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .request-form-button:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 32px rgba(255, 107, 53, 0.5);
        }

        .form-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 1001;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        }

        .form-container {
          background: white;
          border-radius: 16px;
          padding: 32px;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          animation: slideUp 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from {
            transform: translateY(50px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .form-header {
          margin-bottom: 24px;
        }

        .form-header h3 {
          font-size: 24px;
          color: #1a1a1a;
          margin-bottom: 8px;
        }

        .form-header p {
          color: #666;
          font-size: 14px;
        }

        .close-button {
          position: absolute;
          top: 20px;
          right: 20px;
          background: #f5f5f5;
          border: none;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .close-button:hover {
          background: #e0e0e0;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          margin-bottom: 8px;
          color: #333;
          font-weight: 500;
          font-size: 14px;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 15px;
          transition: border-color 0.3s;
          font-family: inherit;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #ff6b35;
        }

        .form-group textarea {
          resize: vertical;
          min-height: 80px;
        }

        .submit-button {
          width: 100%;
          padding: 16px;
          background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.3s;
          box-shadow: 0 6px 20px rgba(255, 107, 53, 0.3);
        }

        .submit-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 28px rgba(255, 107, 53, 0.4);
        }

        .success-message {
          text-align: center;
          padding: 40px 20px;
        }

        .success-message h4 {
          color: #25D366;
          font-size: 20px;
          margin-bottom: 12px;
        }

        .success-message p {
          color: #666;
          font-size: 15px;
        }

        @media (max-width: 768px) {
          .request-form-button {
            bottom: 90px;
            right: 20px;
            padding: 12px 20px;
            font-size: 14px;
          }
        }
      `}</style>

      <button 
        className="request-form-button"
        onClick={() => setIsOpen(true)}
      >
        <Send size={18} />
        {language === 'mr' ? 'सेवा विनंती' : 'Request Service'}
      </button>

      {isOpen && (
        <div className="form-overlay" onClick={() => setIsOpen(false)}>
          <div className="form-container" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={() => setIsOpen(false)}>
              <X size={20} />
            </button>

            {!submitted ? (
              <>
                <div className="form-header">
                  <h3>{t.title}</h3>
                  <p>{t.subtitle}</p>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>{t.name} *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder={language === 'mr' ? 'तुमचे पूर्ण नाव' : 'Enter your Full Name'}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t.phone} *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{10}"
                      placeholder={language === 'mr' ? 'तुमचा फोन नंबर' : 'Enter your Phone Number'}
                    />
                  </div>

                  <div className="form-group">
                    <label>{t.service} *</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                    >
                      <option value="">{language === 'mr' ? 'सेवा निवडा' : 'Choose a service'}</option>
                      {t.services && t.services.map((service, index) => (
                        <option key={index} value={service}>
                          {service}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>{t.message}</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={language === 'mr' ? 'काही विशेष माहिती...' : 'Any specific requirements...'}
                    />
                  </div>

                  <button type="submit" className="submit-button">
                    <Send size={18} />
                    {t.submit}
                  </button>
                </form>
              </>
            ) : (
              <div className="success-message">
                <h4>✓ {t.success}</h4>
                <p>{language === 'mr' ? 'आम्ही २४ तासांत तुमच्याशी संपर्क साधू.' : 'We will contact you within 24 hours.'}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ServiceRequestForm;